'use client'

import type { ElementType, HTMLAttributes } from 'react'

import { cn } from '@/shared/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'

export type TruncatedTooltipTextProps = {
  text: string
  maxChars: number
  className?: string
  as?: ElementType
} & Omit<HTMLAttributes<HTMLElement>, 'children' | 'title'>

function normalizeText(text: string) {
  return text.replace(/\s+/g, ' ').trim()
}

export function truncateTextWithEllipsis(text: string, maxChars: number) {
  const normalized = normalizeText(text)

  if (!maxChars || maxChars < 1 || normalized.length <= maxChars) {
    return normalized
  }

  const truncatedText = normalized.slice(0, maxChars).trimEnd()
  return truncatedText.length > 0 ? `${truncatedText}...` : '...'
}

export default function TruncatedTooltipText({
  text,
  maxChars,
  className,
  as: Comp = 'span',
  ...rest
}: TruncatedTooltipTextProps) {
  const normalizedText = normalizeText(text)
  const displayText = truncateTextWithEllipsis(normalizedText, maxChars)
  const isTruncated = displayText !== normalizedText

  const content = (
    <Comp
      {...rest}
      className={cn('block w-full max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap', className)}
    >
      {displayText}
    </Comp>
  )

  if (!isTruncated) {
    return content
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent>
          <p className="break-words">{normalizedText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
